import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { AlertTriangle, Save, ChevronDown, ChevronUp, Plus, Download, Send, Trash2 } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const usd = (v) => fmtUSD(v, { compact: true });
const CLOUD_COLORS = { AWS: "#ff9900", Azure: "#0078d4", GCP: "#00579e", OCI: "#c74634" };
const err = (e) => e?.response?.data?.detail || e.message;

const downloadExport = async (report, month, fmt) => {
  try {
    const blob = await api.alloc.exportBlob(report, month, fmt);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report}_report_${month}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Export failed", { description: err(e) });
  }
};

function ExportButtons({ report, month }) {
  return (
    <div className="flex items-center gap-2">
      {["csv", "xlsx"].map((fmt) => (
        <Button key={fmt} size="sm" variant="outline" className="h-8 text-xs" data-testid={`export-${report}-${fmt}`}
          onClick={() => downloadExport(report, month, fmt)}>
          <Download className="w-3.5 h-3.5 mr-1.5" /> {fmt.toUpperCase()}
        </Button>
      ))}
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-medium text-muted-foreground whitespace-nowrap ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, mono, className = "" }) => (
  <td className={`px-3 py-2 whitespace-nowrap ${right ? "text-right" : ""} ${mono ? "cs-mono" : ""} ${className}`}>{children}</td>
);

function FundingTab({ month }) {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillApp, setDrillApp] = useState(null);

  useEffect(() => {
    setDrill(null); setDrillApp(null);
    api.alloc.report(month).then(setData).catch((e) => toast.error(err(e)));
  }, [month]);

  const openDrill = (app) => {
    if (drillApp === app) { setDrillApp(null); setDrill(null); return; }
    setDrillApp(app);
    api.alloc.drill(app, month).then(setDrill).catch((e) => toast.error(err(e)));
  };

  if (!data) return <div className="cs-card p-8 text-muted-foreground text-sm" data-testid="alloc-loading">Computing allocations…</div>;
  const rc = data.reconciliation;
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap" data-testid="alloc-recon-strip">
        {[["Cloud Billing Total", usd(rc.cloud_total ?? rc.aws_total)], ["Mapped to Applications", usd(rc.mapped_total)],
          ["Mapped %", `${rc.mapped_pct}%`]].map(([l, v]) => (
          <div key={l} className="cs-card px-4 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{l}</div>
            <div className="cs-mono text-lg font-semibold">{v}</div>
          </div>
        ))}
        <div className="cs-card px-4 py-2.5 border-l-4 border-l-[#f59e0b]" data-testid="alloc-unallocated-kpi">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#92400e]">Unallocated (Exception)</div>
          <div className="cs-mono text-lg font-semibold text-[#92400e]">{usd(rc.unallocated)}</div>
        </div>
        <div className="ml-auto"><ExportButtons report="funding" month={month} /></div>
      </div>

      {!!data.by_cloud?.length && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="alloc-by-cloud-strip">
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mr-1">Net spend by cloud</span>
          {data.by_cloud.map((c) => (
            <span key={c.cloud} data-testid={`alloc-cloud-${c.cloud.toLowerCase()}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-border bg-white text-xs">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: CLOUD_COLORS[c.cloud] || "#94a3b8" }} />
              <span className="font-medium">{c.cloud}</span>
              <span className="cs-mono text-muted-foreground">{usd(c.net_cost)} · {c.share_pct}%</span>
            </span>
          ))}
        </div>
      )}

      <div className="cs-card overflow-x-auto" data-testid="alloc-apps-table">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr><Th>Application</Th><Th right>Direct Cost</Th><Th right>Shared Allocated</Th><Th right>True App Cost</Th>
              <Th right>CMS %</Th><Th right>CMS $</Th><Th right>State %</Th><Th right>State $</Th><Th /></tr>
          </thead>
          <tbody>
            {data.apps.map((a) => (
              <React.Fragment key={a.app}>
                <tr className="cs-row-hover border-b border-border cursor-pointer" onClick={() => openDrill(a.app)} data-testid={`alloc-app-row-${a.app.toLowerCase()}`}>
                  <Td className="font-semibold">{a.app}</Td>
                  <Td right mono>{usd(a.direct)}</Td>
                  <Td right mono className="text-[#6d28d9]">{usd(a.shared_allocated)}</Td>
                  <Td right mono className="font-semibold text-[#00579e]">{usd(a.total)}</Td>
                  <Td right mono>{a.cms_pct}%</Td>
                  <Td right mono>{usd(a.cms_amount)}</Td>
                  <Td right mono>{a.state_pct}%</Td>
                  <Td right mono>{usd(a.state_amount)}</Td>
                  <Td right>{drillApp === a.app ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}</Td>
                </tr>
                {drillApp === a.app && drill && (
                  <tr><td colSpan={9} className="bg-[hsl(var(--cs-tint))] px-4 py-4" data-testid={`alloc-drill-${a.app.toLowerCase()}`}>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2">Direct resources · Cloud → Environment → Account → Service → Resource</div>
                        <table className="w-full text-xs bg-white rounded-sm border border-border">
                          <thead><tr><Th>Cloud</Th><Th>Env</Th><Th>Account</Th><Th>Service</Th><Th>Resource</Th><Th right>Net Cost</Th></tr></thead>
                          <tbody>{drill.direct_rows.map((r, i) => (
                            <tr key={i} className="border-t border-border">
                              <Td><span className="font-medium" style={{ color: CLOUD_COLORS[r.cloud] || "inherit" }}>{r.cloud}</span></Td>
                              <Td>{r.environment}</Td><Td>{r.account}</Td><Td>{r.service}</Td>
                              <Td mono className="text-muted-foreground">{r.resource_id}</Td>
                              <Td right mono>{usd(r.net_cost)}</Td>
                            </tr>))}</tbody>
                        </table>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2">Shared allocations received</div>
                        <table className="w-full text-xs bg-white rounded-sm border border-border">
                          <thead><tr><Th>Shared Component</Th><Th>Method</Th><Th right>Your %</Th><Th right>Allocated</Th></tr></thead>
                          <tbody>{drill.shared_allocations.map((s, i) => (
                            <tr key={i} className="border-t border-border">
                              <Td>{s.shared_group}</Td><Td mono>{s.method}</Td>
                              <Td right mono>{s.your_pct}%</Td><Td right mono className="text-[#6d28d9]">{usd(s.your_amount)}</Td>
                            </tr>))}</tbody>
                        </table>
                      </div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="cs-card p-5" data-testid="alloc-shared-pools">
          <h3 className="cs-heading text-sm font-semibold mb-3">Shared Cost Pools</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><Th>Component</Th><Th>Method</Th><Th right>Pool (Net)</Th><Th>Splits</Th></tr></thead>
              <tbody>{data.shared_detail.map((s) => (
                <tr key={s.shared_group} className="border-t border-border align-top">
                  <Td className="font-medium">{s.shared_group}</Td>
                  <Td mono>{s.method}</Td>
                  <Td right mono>{usd(s.pool_net)}</Td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(s.splits).map(([k, v]) => (
                        <span key={k} className="cs-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-[hsl(var(--cs-tint))] border border-border whitespace-nowrap">
                          {k} <span className="font-semibold">{v}%</span>
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>
        <div className="cs-card p-5 border-l-4 border-l-[#f59e0b]" data-testid="alloc-exception-bucket">
          <h3 className="cs-heading text-sm font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#f59e0b]" /> Unallocated Exception Bucket
          </h3>
          <p className="text-[11px] text-muted-foreground mb-3">Spend not matched by any bundle rule — never silently assigned. Fix tags or extend a bundle in Rules.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><Th>Cloud</Th><Th>Account</Th><Th>Service</Th><Th>App Tag</Th><Th>Cost Center</Th><Th right>Net Cost</Th></tr></thead>
              <tbody>{data.unallocated.rows.map((r, i) => (
                <tr key={i} className="border-t border-border">
                  <Td><span className="font-medium" style={{ color: CLOUD_COLORS[r.cloud] || "inherit" }}>{r.cloud}</span></Td>
                  <Td>{r.account}</Td><Td>{r.service}</Td>
                  <Td mono className="text-[#b91c1c]">{r.app_tag || "—"}</Td><Td mono>{r.cost_center}</Td>
                  <Td right mono>{usd(r.net_cost)}</Td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function WaterfallTab({ month }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.alloc.waterfall(month).then(setData).catch((e) => toast.error(err(e))); }, [month]);
  if (!data) return <div className="cs-card p-8 text-muted-foreground text-sm">Building waterfall…</div>;
  const max = Math.max(...data.stages.map((s) => Math.abs(s.value)));
  return (
    <div className="space-y-5">
      <div className="cs-card p-5" data-testid="alloc-waterfall-chart">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="cs-heading text-sm font-semibold mb-1">Commercial Cost Waterfall · {data.month}</h3>
            <p className="text-[11px] text-muted-foreground mb-4">Cloud List → Discount & Commitment Benefit → Net Cloud → Managed-Service Cost → State Charge → Margin</p>
          </div>
          <ExportButtons report="margin" month={month} />
        </div>
        <div className="space-y-2.5">
          {data.stages.map((s) => (
            <div key={s.stage} className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground w-56 shrink-0">{s.stage}</span>
              <div className="flex-1 h-7 bg-[hsl(var(--cs-tint))] rounded-sm overflow-hidden">
                <div className="h-full rounded-sm flex items-center px-2 transition-all duration-500"
                  style={{ width: `${Math.max(Math.abs(s.value) / max * 100, 10)}%`, background: s.color }}>
                  <span className="cs-mono text-[11px] text-white font-semibold whitespace-nowrap">
                    {s.value < 0 ? "−" : ""}{usd(Math.abs(s.value))}
                  </span>
                </div>
              </div>
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground w-14">{s.kind}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="cs-card overflow-x-auto" data-testid="alloc-margin-table">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr><Th>Application</Th><Th right>List Cost</Th><Th right>Discount</Th><Th right>Disc %</Th><Th right>Net Cloud</Th>
              <Th right>MS Cost</Th><Th right>State Charge</Th><Th right>Disc Shared</Th><Th right>Disc Retained</Th>
              <Th right>Margin $</Th><Th right>Margin %</Th></tr>
          </thead>
          <tbody>
            {data.apps.map((a) => (
              <tr key={a.app} className="cs-row-hover border-b border-border" data-testid={`alloc-margin-row-${a.app.toLowerCase()}`}>
                <Td className="font-semibold">{a.app}</Td>
                <Td right mono>{usd(a.list_cost)}</Td>
                <Td right mono className="text-[#15803d]">−{usd(a.discount)}</Td>
                <Td right mono>{a.discount_pct}%</Td>
                <Td right mono className="font-semibold">{usd(a.net_cost)}</Td>
                <Td right mono>{usd(a.ms_cost)}</Td>
                <Td right mono className="text-[#0e7490]">{usd(a.state_charge)}</Td>
                <Td right mono>{usd(a.discount_shared)}</Td>
                <Td right mono>{usd(a.discount_retained)}</Td>
                <Td right mono className="font-semibold text-[#be185d]">{usd(a.margin)}</Td>
                <Td right mono className="font-semibold text-[#be185d]">{a.margin_pct}%</Td>
              </tr>
            ))}
            <tr className="bg-[hsl(var(--cs-tint))] font-semibold" data-testid="alloc-margin-totals">
              <Td>Total</Td>
              <Td right mono>{usd(data.totals.list_cost)}</Td>
              <Td right mono className="text-[#15803d]">−{usd(data.totals.discount)}</Td>
              <Td right mono>{data.totals.discount_pct}%</Td>
              <Td right mono>{usd(data.totals.net_cost)}</Td>
              <Td right mono>{usd(data.totals.ms_cost)}</Td>
              <Td right mono>{usd(data.totals.state_charge)}</Td>
              <Td right mono>{usd(data.totals.discount_shared)}</Td>
              <Td right mono>{usd(data.totals.discount_retained)}</Td>
              <Td right mono className="text-[#be185d]">{usd(data.totals.margin)}</Td>
              <Td right mono className="text-[#be185d]">{data.totals.margin_pct}%</Td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Policy: managed-service cost ≈ {data.policy.ms_pct}% of net · {data.policy.discount_share_pct}% of discounts passed through (shared) by default — configurable per engagement.
      </p>
    </div>
  );
}

function RulesTab({ onChanged }) {
  const [rules, setRules] = useState(null);
  const [fNew, setFNew] = useState({ app: "Eligibility", cms_pct: "90", state_pct: "10", effective_from: "" });
  const load = () => api.alloc.rules().then(setRules).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, []);
  if (!rules) return <div className="cs-card p-8 text-muted-foreground text-sm">Loading rules…</div>;

  const saveBundle = async (b) => {
    try { await api.alloc.saveBundle(b); toast.success(`${b.app} bundle saved`); onChanged(); }
    catch (e) { toast.error(err(e)); }
  };
  const saveShared = async (s) => {
    try { await api.alloc.saveShared(s); toast.success(`${s.shared_group} rule saved`); onChanged(); }
    catch (e) { toast.error("Save failed", { description: err(e) }); }
  };
  const addFunding = async () => {
    if (!fNew.effective_from) return toast.error("Set an effective month (YYYY-MM)");
    try {
      await api.alloc.addFunding({ app: fNew.app, cms_pct: Number(fNew.cms_pct), state_pct: Number(fNew.state_pct), effective_from: fNew.effective_from });
      toast.success("Funding rule added"); load(); onChanged();
    } catch (e) { toast.error("Save failed", { description: err(e) }); }
  };

  return (
    <div className="space-y-5" data-testid="alloc-rules-tab">
      <div className="cs-card p-5" data-testid="alloc-bundles-card">
        <h3 className="cs-heading text-sm font-semibold mb-1">Business Bundles / Virtual Tags</h3>
        <p className="text-[11px] text-muted-foreground mb-4">An application matches a resource if its tag OR cost center OR account matches — reporting doesn't depend on perfect AWS tagging.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rules.bundles.map((b) => (
            <BundleEditor key={b.id} bundle={b} onSave={saveBundle} />
          ))}
        </div>
      </div>

      <div className="cs-card p-5" data-testid="alloc-shared-rules-card">
        <h3 className="cs-heading text-sm font-semibold mb-1">Shared Cost Allocation Rules</h3>
        <p className="text-[11px] text-muted-foreground mb-4">Fixed percentages must total 100%. Usage-based allocates proportional to each app's direct spend. Custom Driver uses an uploaded business metric. All rules are versioned.</p>
        <div className="space-y-3">
          {rules.shared_rules.map((s) => (
            <SharedEditor key={s.id} rule={s} apps={rules.apps} drivers={rules.drivers || []} onSave={saveShared} />
          ))}
        </div>
      </div>

      <DriversCard drivers={rules.drivers || []} apps={rules.apps} onChanged={() => { load(); onChanged(); }} />

      <div className="cs-card p-5" data-testid="alloc-funding-card">
        <h3 className="cs-heading text-sm font-semibold mb-1">Funding Rules (effective-dated)</h3>
        <p className="text-[11px] text-muted-foreground mb-4">CMS % + State % must equal 100. History is preserved — prior periods reproduce their approved splits.</p>
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <Select value={fNew.app} onValueChange={(v) => setFNew((p) => ({ ...p, app: v }))}>
            <SelectTrigger className="h-8 w-[150px] text-xs rounded-sm" data-testid="funding-app-select"><SelectValue /></SelectTrigger>
            <SelectContent>{rules.apps.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="h-8 w-20 text-xs" data-testid="funding-cms-input" value={fNew.cms_pct} onChange={(e) => setFNew((p) => ({ ...p, cms_pct: e.target.value, state_pct: String(100 - Number(e.target.value) || 0) }))} placeholder="CMS %" />
          <span className="text-xs text-muted-foreground">CMS % /</span>
          <Input className="h-8 w-20 text-xs" data-testid="funding-state-input" value={fNew.state_pct} onChange={(e) => setFNew((p) => ({ ...p, state_pct: e.target.value }))} placeholder="State %" />
          <span className="text-xs text-muted-foreground">State % · effective</span>
          <Input className="h-8 w-28 text-xs" data-testid="funding-effective-input" value={fNew.effective_from} onChange={(e) => setFNew((p) => ({ ...p, effective_from: e.target.value }))} placeholder="YYYY-MM" />
          <Button size="sm" className="h-8 bg-[#00579e] hover:bg-[#00477f]" onClick={addFunding} data-testid="funding-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Rule
          </Button>
        </div>
        <table className="w-full text-xs" data-testid="funding-history-table">
          <thead><tr><Th>Application</Th><Th right>CMS %</Th><Th right>State %</Th><Th right>Effective From</Th></tr></thead>
          <tbody>{rules.funding_rules.map((f) => (
            <tr key={f.id} className="border-t border-border">
              <Td className="font-medium">{f.app}</Td>
              <Td right mono>{f.cms_pct}%</Td><Td right mono>{f.state_pct}%</Td>
              <Td right mono className="text-muted-foreground">{f.effective_from}</Td>
            </tr>))}</tbody>
        </table>
      </div>

      <ScheduleCard />
    </div>
  );
}

function DriversCard({ drivers, apps, onChanged }) {
  const [newName, setNewName] = useState("");
  const [newUnit, setNewUnit] = useState("");
  return (
    <div className="cs-card p-5" data-testid="alloc-drivers-card">
      <h3 className="cs-heading text-sm font-semibold mb-1">Custom Usage Drivers</h3>
      <p className="text-[11px] text-muted-foreground mb-4">Business metrics (API calls, transactions…) that drive usage-based shared allocation — pick "Custom Driver" on any shared rule above.</p>
      <div className="space-y-3">
        {drivers.map((d) => <DriverEditor key={d.id} driver={d} apps={apps} onChanged={onChanged} />)}
      </div>
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
        <Input className="h-8 w-52 text-xs" placeholder="New driver name (e.g. Transactions)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="driver-new-name" />
        <Input className="h-8 w-36 text-xs" placeholder="Unit (e.g. K/mo)" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} data-testid="driver-new-unit" />
        <Button size="sm" className="h-8 bg-[#00579e] hover:bg-[#00477f]" data-testid="driver-add-btn" onClick={async () => {
          if (!newName.trim()) return toast.error("Give the driver a name");
          try {
            await api.alloc.saveDriver({ name: newName.trim(), unit: newUnit.trim(), values: Object.fromEntries(apps.map((a) => [a, 1])) });
            setNewName(""); setNewUnit(""); toast.success("Driver added — set its per-app values"); onChanged();
          } catch (e) { toast.error(err(e)); }
        }}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Driver
        </Button>
      </div>
    </div>
  );
}

function DriverEditor({ driver, apps, onChanged }) {
  const [vals, setVals] = useState(() => Object.fromEntries(apps.map((a) => [a, String(driver.values?.[a] ?? 0)])));
  const slug = driver.name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
  return (
    <div className="border border-border rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid={`driver-${slug}`}>
      <span className="text-sm font-medium w-44 shrink-0">{driver.name}
        <span className="cs-mono text-[10px] text-muted-foreground ml-1">{driver.unit} · v{driver.version}</span>
      </span>
      {apps.map((a) => (
        <label key={a} className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {a.slice(0, 4)}
          <Input className="h-7 w-16 text-xs" value={vals[a]} data-testid={`driver-val-${slug}-${a.toLowerCase()}`}
            onChange={(e) => setVals((p) => ({ ...p, [a]: e.target.value }))} />
        </label>
      ))}
      <div className="ml-auto flex items-center gap-1.5">
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`driver-save-${slug}`} onClick={async () => {
          try {
            await api.alloc.saveDriver({ id: driver.id, name: driver.name, unit: driver.unit, values: Object.fromEntries(apps.map((a) => [a, Number(vals[a]) || 0])) });
            toast.success(`${driver.name} saved`); onChanged();
          } catch (e) { toast.error("Save failed", { description: err(e) }); }
        }}>
          <Save className="w-3 h-3 mr-1" /> Save
        </Button>
        <button className="p-1 text-[#b91c1c] hover:opacity-70" data-testid={`driver-delete-${slug}`} onClick={async () => {
          try { await api.alloc.deleteDriver(driver.id); toast.success("Driver deleted"); onChanged(); }
          catch (e) { toast.error("Cannot delete", { description: err(e) }); }
        }}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ScheduleCard() {
  const [sched, setSched] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => api.alloc.schedule().then((s) => { setSched(s); setRecipients(s.recipients.join(", ")); });
  useEffect(() => { load(); }, []);
  if (!sched) return null;

  const save = async (patch) => {
    try {
      const updated = await api.alloc.saveSchedule({
        report: patch.report ?? sched.report,
        recipients: (patch.recipients ?? recipients).split(",").map((x) => x.trim()).filter(Boolean),
        enabled: patch.enabled ?? sched.enabled,
      });
      setSched(updated); setRecipients(updated.recipients.join(", "));
      toast.success("Schedule saved");
    } catch (e) { toast.error("Save failed", { description: err(e) }); }
  };

  return (
    <div className="cs-card p-5" data-testid="alloc-schedule-card">
      <h3 className="cs-heading text-sm font-semibold mb-1">Scheduled Period-Close Report</h3>
      <p className="text-[11px] text-muted-foreground mb-4">
        When a billing period closes, CloudScore emails the reconciled report (with CSV attached) to program owners automatically. Sent mail lands in the PartnerOps outbox.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={sched.report} onValueChange={(v) => save({ report: v })}>
          <SelectTrigger className="h-8 w-[190px] text-xs rounded-sm" data-testid="schedule-report-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="funding">CMS/State Funding Report</SelectItem>
            <SelectItem value="margin">Discount & Margin Report</SelectItem>
          </SelectContent>
        </Select>
        <Input className="h-8 w-72 text-xs" placeholder="recipients, comma-separated" value={recipients}
          onChange={(e) => setRecipients(e.target.value)} onBlur={() => save({})} data-testid="schedule-recipients-input" />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={sched.enabled} onCheckedChange={(c) => save({ enabled: c })} data-testid="schedule-enabled-switch" />
          Auto-send on period close
        </label>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={sending} data-testid="schedule-send-now-btn" onClick={async () => {
          setSending(true);
          try {
            const out = await api.alloc.sendNow();
            toast.success(`Report for ${out.period} sent to ${out.results.length} recipient(s)`, { description: out.results.map((r) => `${r.to}: ${r.status}`).join(" · ") });
            load();
          } catch (e) { toast.error("Send failed", { description: err(e) }); }
          finally { setSending(false); }
        }}>
          <Send className="w-3.5 h-3.5 mr-1.5" /> Send Now
        </Button>
        {sched.last_sent_period && (
          <span className="cs-mono text-[11px] text-muted-foreground" data-testid="schedule-last-sent">last sent: {sched.last_sent_period}</span>
        )}
      </div>
    </div>
  );
}

function BundleEditor({ bundle, onSave }) {
  const [tags, setTags] = useState(bundle.tag_values.join(", "));
  const [ccs, setCcs] = useState(bundle.cost_centers.join(", "));
  const [accts, setAccts] = useState(bundle.accounts.join(", "));
  const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  return (
    <div className="border border-border rounded-sm p-3 space-y-2" data-testid={`bundle-${bundle.app.toLowerCase()}`}>
      <div className="text-sm font-semibold text-[#00579e]">{bundle.app} <span className="cs-mono text-[10px] text-muted-foreground font-normal">v{bundle.version}</span></div>
      {[["Tag values", tags, setTags, "bundle-tags"], ["Cost centers", ccs, setCcs, "bundle-ccs"], ["Accounts", accts, setAccts, "bundle-accounts"]].map(([l, v, set, tid]) => (
        <div key={l} className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-20 shrink-0">{l}</span>
          <Input className="h-7 text-xs" value={v} onChange={(e) => set(e.target.value)} data-testid={`${tid}-${bundle.app.toLowerCase()}`} />
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`bundle-save-${bundle.app.toLowerCase()}`}
        onClick={() => onSave({ id: bundle.id, app: bundle.app, tag_values: split(tags), cost_centers: split(ccs), accounts: split(accts) })}>
        <Save className="w-3 h-3 mr-1" /> Save
      </Button>
    </div>
  );
}

function SharedEditor({ rule, apps, drivers, onSave }) {
  const [method, setMethod] = useState(rule.method);
  const [driverId, setDriverId] = useState(rule.driver_id || drivers[0]?.id || "");
  const [splits, setSplits] = useState(() => Object.fromEntries(apps.map((a) => [a, String(rule.splits?.[a] ?? 0)])));
  const total = apps.reduce((s, a) => s + (Number(splits[a]) || 0), 0);
  return (
    <div className="border border-border rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid={`shared-rule-${rule.shared_group.toLowerCase()}`}>
      <span className="text-sm font-medium w-44 shrink-0">{rule.shared_group} <span className="cs-mono text-[10px] text-muted-foreground">v{rule.version}</span></span>
      <Select value={method} onValueChange={setMethod}>
        <SelectTrigger className="h-7 w-[130px] text-xs rounded-sm" data-testid={`shared-method-${rule.shared_group.toLowerCase()}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="fixed_pct">Fixed %</SelectItem>
          <SelectItem value="equal">Equal Split</SelectItem>
          <SelectItem value="usage">Usage-Based</SelectItem>
          <SelectItem value="custom_driver">Custom Driver</SelectItem>
        </SelectContent>
      </Select>
      {method === "custom_driver" && (
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="h-7 w-[160px] text-xs rounded-sm" data-testid={`shared-driver-${rule.shared_group.toLowerCase()}`}><SelectValue placeholder="Pick driver" /></SelectTrigger>
          <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {method === "fixed_pct" && (
        <>
          {apps.map((a) => (
            <label key={a} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              {a.slice(0, 4)}
              <Input className="h-7 w-14 text-xs" value={splits[a]} data-testid={`shared-pct-${rule.shared_group.toLowerCase()}-${a.toLowerCase()}`}
                onChange={(e) => setSplits((p) => ({ ...p, [a]: e.target.value }))} />
            </label>
          ))}
          <span className={`cs-mono text-xs font-semibold ${Math.abs(total - 100) < 0.01 ? "text-[#15803d]" : "text-[#b91c1c]"}`} data-testid={`shared-total-${rule.shared_group.toLowerCase()}`}>
            Σ {total}%
          </span>
        </>
      )}
      <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" data-testid={`shared-save-${rule.shared_group.toLowerCase()}`}
        onClick={() => onSave({ id: rule.id, method, driver_id: method === "custom_driver" ? driverId : null, splits: Object.fromEntries(apps.map((a) => [a, Number(splits[a]) || 0])) })}>
        <Save className="w-3 h-3 mr-1" /> Save
      </Button>
    </div>
  );
}

export default function AppAllocation() {
  const [tab, setTab] = useState("funding");
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.alloc.months().then((d) => { setMonths(d.months); setMonth(d.months[d.months.length - 1]); }).catch(console.error);
  }, []);

  const TABS = [
    { id: "funding", l: "Application Costs & Funding" },
    { id: "waterfall", l: "Discount & Margin Waterfall" },
    { id: "rules", l: "Rules" },
  ];

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="app-allocation-page">
      <div className="flex items-center gap-1.5 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`alloc-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${
              tab === t.id ? "border-[#00579e] text-[#00579e]" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>{t.l}</button>
        ))}
        {tab !== "rules" && month && (
          <div className="ml-auto pb-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-8 w-[130px] text-xs rounded-sm" data-testid="alloc-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
      {tab === "funding" && month && <FundingTab key={`f-${month}-${refreshKey}`} month={month} />}
      {tab === "waterfall" && month && <WaterfallTab key={`w-${month}-${refreshKey}`} month={month} />}
      {tab === "rules" && <RulesTab onChanged={() => setRefreshKey((k) => k + 1)} />}
    </div>
  );
}
